//? React
import { useEffect, useState } from "react";

//? External
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { FaHeart, FaRegHeart, FaPlus } from "react-icons/fa";

//* Data hook + API
import { useQuestionLists } from "../../hooks/questionLists/useQuestionLists";
import { questionListsAPI } from "../../hooks/questionLists/questionListsAPI";

interface SaveToListModalProps {
  show: boolean;
  onHide: () => void;
  questionId: string | null;
  token: string;
}

/**
 * Airbnb-style "save to list" modal: lists the user's named lists with a
 * membership toggle for the given question, plus an inline "create new list"
 * field that creates the list and already saves the question into it.
 */
export default function SaveToListModal({
  show,
  onHide,
  questionId,
  token,
}: SaveToListModalProps) {
  const { lists, loading, createList, addItem, removeItem } =
    useQuestionLists(token);

  // Conjunto de ids de listas que já contêm a questão atual. Não há endpoint
  // de pertencimento leve, então buscamos o detalhe de cada lista ao abrir.
  const [memberListIds, setMemberListIds] = useState<Set<string>>(new Set());
  const [checkingMembership, setCheckingMembership] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const [newListName, setNewListName] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!show || !questionId || lists.length === 0) {
      setMemberListIds(new Set());
      return;
    }

    let cancelled = false;
    setCheckingMembership(true);

    Promise.all(lists.map((list) => questionListsAPI.getById(list.id, token)))
      .then((details) => {
        if (cancelled) return;
        const ids = new Set<string>();
        details.forEach((detail) => {
          if (detail.questions.some((q) => q.id === questionId)) {
            ids.add(detail.id);
          }
        });
        setMemberListIds(ids);
      })
      .catch((error) => {
        console.error("Erro ao verificar listas da questão:", error);
      })
      .finally(() => {
        if (!cancelled) setCheckingMembership(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, questionId, lists, token]);

  const handleToggle = async (listId: string) => {
    if (!questionId) return;
    setTogglingId(listId);
    try {
      const isMember = memberListIds.has(listId);
      if (isMember) {
        await removeItem({ listId, questionId });
        setMemberListIds((prev) => {
          const next = new Set(prev);
          next.delete(listId);
          return next;
        });
      } else {
        await addItem({ listId, questionId });
        setMemberListIds((prev) => new Set(prev).add(listId));
      }
    } catch (error) {
      console.error("Erro ao atualizar a lista:", error);
    } finally {
      setTogglingId(null);
    }
  };

  const handleCreate = async () => {
    const name = newListName.trim();
    if (!name || !questionId) return;
    setCreating(true);
    try {
      const created = await createList(name);
      await addItem({ listId: created.id, questionId });
      setMemberListIds((prev) => new Set(prev).add(created.id));
      setNewListName("");
    } catch (error) {
      console.error("Erro ao criar a lista:", error);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaRegHeart className="me-2" />
          Salvar em lista
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="d-flex flex-column gap-3">
        {loading || checkingMembership ? (
          <div className="d-flex justify-content-center py-3">
            <Spinner animation="border" role="status" />
          </div>
        ) : lists.length === 0 ? (
          <p className="text-muted mb-0">
            Você ainda não tem listas. Crie uma abaixo.
          </p>
        ) : (
          <ul className="list-unstyled d-flex flex-column gap-2 mb-0">
            {lists.map((list) => {
              const isMember = memberListIds.has(list.id);
              return (
                <li key={list.id}>
                  <Button
                    id={`btn-toggle-list-${list.id}`}
                    type="button"
                    variant={isMember ? "danger" : "outline-secondary"}
                    className="w-100 d-flex justify-content-between align-items-center"
                    disabled={togglingId === list.id}
                    onClick={() => handleToggle(list.id)}
                  >
                    <span>{list.name}</span>
                    {togglingId === list.id ? (
                      <Spinner animation="border" size="sm" />
                    ) : isMember ? (
                      <FaHeart />
                    ) : (
                      <FaRegHeart />
                    )}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}

        <Form.Group className="d-flex gap-2 align-items-end pt-2 border-top">
          <div className="flex-grow-1">
            <Form.Label className="mb-1">Criar nova lista</Form.Label>
            <Form.Control
              type="text"
              placeholder="Nome da lista"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleCreate();
                }
              }}
            />
          </div>
          <Button
            id="btn-create-list"
            type="button"
            variant="primary"
            aria-label="Criar lista e salvar questão"
            disabled={creating || !newListName.trim()}
            onClick={handleCreate}
          >
            {creating ? <Spinner animation="border" size="sm" /> : <FaPlus />}
          </Button>
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button id="btn-close-save-to-list" type="button" variant="secondary" onClick={onHide}>
          Concluído
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
